import {
  InsightActionKind,
  InsightCitationKind,
  InsightSource,
  InsightTargetPanel,
} from 'models/projects/projectEnums';

//
// One entity's advice as the card renders it: the sentences the model wrote, the sources each
// sentence rests on, and what the reader can do next. Mirror of Contracts/AI/DataModels/AIInsight.
//
// The sentence text is the only prose here and it is already in the reader's language. Everything
// else is a code the client translates itself.
//

/** Where an action lands. Which fields are filled follows from the action's kind. */
export class InsightTarget {
  entityType?: number;

  entityId?: number;

  roomKey?: string;

  taskId?: number;

  panel?: InsightTargetPanel;

  isEqual(other: InsightTarget): boolean {
    return (
      this.entityType === other.entityType &&
      this.entityId === other.entityId &&
      this.roomKey === other.roomKey &&
      this.taskId === other.taskId &&
      this.panel === other.panel
    );
  }
}

/** One thing the reader can do about an insight, as a code and a target rather than a label. */
export class InsightAction {
  kind: InsightActionKind;

  primary: boolean;

  target?: InsightTarget;

  reasonKey?: string;

  reasonValue?: string;

  constructor(kind: InsightActionKind) {
    this.kind = kind;
    this.primary = false;
  }

  isEqual(other: InsightAction): boolean {
    if (this.kind !== other.kind) return false;
    if (this.primary !== other.primary) return false;
    if (this.reasonKey !== other.reasonKey) return false;
    if (this.reasonValue !== other.reasonValue) return false;
    if ((this.target === undefined) !== (other.target === undefined)) return false;

    return this.target === undefined || other.target === undefined || this.target.isEqual(other.target);
  }
}

/** One thing a sentence rests on: an open deliverable, a document, a clause, or a record. */
export class InsightCitation {
  key: string;

  kind: InsightCitationKind;

  marker: number;

  isoControlId?: number;

  entityType?: number;

  entityId?: number;

  linkId?: number;

  version?: string;

  roomKey?: string;

  taskId?: number;

  value?: string;

  constructor(key: string, kind: InsightCitationKind, marker: number) {
    this.key = key;
    this.kind = kind;
    this.marker = marker;
  }

  isEqual(other: InsightCitation): boolean {
    return (
      this.key === other.key &&
      this.kind === other.kind &&
      this.marker === other.marker &&
      this.isoControlId === other.isoControlId &&
      this.entityType === other.entityType &&
      this.entityId === other.entityId &&
      this.linkId === other.linkId &&
      this.version === other.version &&
      this.roomKey === other.roomKey &&
      this.taskId === other.taskId &&
      this.value === other.value
    );
  }
}

/** One sentence and the citation keys it rests on, in the order the model named them. */
export class InsightSentence {
  text: string;

  citationKeys: string[];

  constructor(text: string) {
    this.text = text;
    this.citationKeys = [];
  }

  isEqual(other: InsightSentence): boolean {
    if (this.text !== other.text) return false;
    if (this.citationKeys.length !== other.citationKeys.length) return false;

    return this.citationKeys.every((key, index) => key === other.citationKeys[index]);
  }
}

export class Insight {
  source: InsightSource;

  failureReason?: string;

  sentences: InsightSentence[];

  citations: InsightCitation[];

  actions: InsightAction[];

  generatedAt?: Date;

  stateHash?: string;

  constructor(source: InsightSource) {
    this.source = source;
    this.sentences = [];
    this.citations = [];
    this.actions = [];
  }

  /** The whole answer as one string: what a screen reader is given, and what a tooltip repeats. */
  getText(): string {
    return this.sentences.map((sentence) => sentence.text).join(' ');
  }

  /**
   * The answer as markdown paragraphs, one per row it is about: sentences are grouped by the first
   * `item:` key they rest on, in the order the model wrote them, each paragraph opened by that row's
   * own name in bold. Sentences without an item key stay with the paragraph before them.
   */
  getParagraphs(): string {
    const paragraphs: string[] = [];
    let currentKey: string | undefined;
    let current: string[] = [];

    const flush = () => {
      if (current.length === 0) return;
      const name = this.citations.find((citation) => citation.key === currentKey)?.value;
      paragraphs.push(name === undefined || name.length === 0 ? current.join(' ') : `**${name}** ${current.join(' ')}`);
      current = [];
    };

    for (const sentence of this.sentences) {
      const key = sentence.citationKeys.find((candidate) => candidate.startsWith('item:'));
      if (key !== undefined && key !== currentKey) {
        flush();
        currentKey = key;
      }
      current.push(sentence.text);
    }
    flush();

    return paragraphs.join('\n\n');
  }

  isEqual(other: Insight): boolean {
    if (this.source !== other.source) return false;
    if (this.failureReason !== other.failureReason) return false;
    if (this.stateHash !== other.stateHash) return false;
    if (this.generatedAt?.getTime() !== other.generatedAt?.getTime()) return false;
    if (this.sentences.length !== other.sentences.length) return false;
    if (this.citations.length !== other.citations.length) return false;
    if (this.actions.length !== other.actions.length) return false;
    if (!this.sentences.every((sentence, index) => sentence.isEqual(other.sentences[index]))) return false;
    if (!this.citations.every((citation, index) => citation.isEqual(other.citations[index]))) return false;

    return this.actions.every((action, index) => action.isEqual(other.actions[index]));
  }
}
